import { Button } from '@capturi/ui-components'
import { BaseModalComponentProps } from '@capturi/use-modal'
import {
  Box,
  Heading,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Tag,
  TagCloseButton,
  TagLabel,
  Textarea,
  Tooltip,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react'
import { Trans, t } from '@lingui/macro'
import { FC, useMemo, useRef, useState } from 'react'
import isEqual from 'react-fast-compare'

type Props = {
  queues: string[]
  prompt: string
  existingQueues: Set<string>
  duplicatedQueues: Set<string>
  isNew?: boolean
  handleAddOrEditQueuePromptPair: (
    queues: string[],
    prompt: string,
    index?: number,
  ) => void
}

export const QueueAndPromptModal: FC<Props & BaseModalComponentProps> = ({
  queues,
  prompt,
  existingQueues,
  duplicatedQueues,
  isNew,
  onClose,
  handleAddOrEditQueuePromptPair,
}) => {
  const [newQueues, setNewQueues] = useState<string[]>(queues ?? [])
  const inputRef = useRef<HTMLInputElement>(null)
  const [queueInput, setQueueInput] = useState('')
  const [promptInput, setPromptInput] = useState(prompt ?? '')

  const cleanQueues = useMemo(() => {
    const cleanQueues = new Set<string>(existingQueues)
    queues.forEach((queue) => {
      cleanQueues.delete(queue)
    })
    return cleanQueues.union(duplicatedQueues)
  }, [queues, existingQueues, duplicatedQueues])

  const isDisabled = useMemo(
    () => isEqual(newQueues, queues) && isEqual(promptInput, prompt),
    [newQueues, queues, promptInput, prompt],
  )
  const handleAdd = (): void => {
    const trimmedValue = queueInput.trim()
    if (trimmedValue) {
      addQueues(trimmedValue)
      setQueueInput('')
    }
  }

  const addQueues = (queues: string): void => {
    const tmpQueues = queues.split(',').map((queue) => queue.trim())
    setNewQueues([...new Set([...newQueues, ...tmpQueues])])
  }

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>): void => {
    event.preventDefault()
    const pasteData = event.clipboardData.getData('Text')
    addQueues(pasteData)
  }

  const handleQueueInputKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ): void => {
    if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault()
      handleAdd()
    } else if (
      event.key === 'Backspace' &&
      queueInput === '' &&
      (newQueues?.length ?? 0) > 0
    ) {
      event.preventDefault()
      setNewQueues(newQueues.slice(0, -1))
    }
  }

  const handleRemoveQueue = (queue: string) => {
    const spliced = newQueues.toSpliced(newQueues.indexOf(queue), 1)
    setNewQueues(spliced)
  }

  const handlePromptChange = (prompt: string) => {
    setPromptInput(prompt)
  }

  return (
    <Modal size="2xl" isOpen={true} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalHeader>
          {!queues?.length ? t`Add queue and prompt` : t`Edit queue and prompt`}
        </ModalHeader>
        <ModalBody>
          <VStack gap={5}>
            <Box w="100%">
              <Heading size="md">
                <Trans>Queues</Trans>
              </Heading>
              <Box position="relative">
                <Box
                  border="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                  p={2}
                  minH="100px"
                  onClick={() => inputRef.current?.focus()}
                  _focusWithin={{
                    borderColor: 'primary.500',
                    shadow: 'extended',
                  }}
                >
                  <Wrap spacing={2}>
                    {newQueues.map((queue, index) => (
                      <WrapItem key={index}>
                        <Tooltip
                          isDisabled={!cleanQueues.has(queue)}
                          hasArrow
                          placement="top"
                          label={t`This queue is duplicated. Remove it or its duplicate(s) to be able to save.`}
                        >
                          <Tag
                            size="md"
                            borderRadius="full"
                            colorScheme={
                              cleanQueues.has(queue) ? 'red' : 'gray'
                            }
                          >
                            <TagLabel>{queue}</TagLabel>
                            <TagCloseButton
                              onClick={() => handleRemoveQueue(queue)}
                            />
                          </Tag>
                        </Tooltip>
                      </WrapItem>
                    ))}
                    <WrapItem>
                      <Input
                        ref={inputRef}
                        placeholder="Add queue..."
                        size="sm"
                        variant="unstyled"
                        value={queueInput}
                        onBlur={handleAdd}
                        onPaste={handlePaste}
                        onChange={(e) => setQueueInput(e.target.value)}
                        onKeyDown={handleQueueInputKeyDown}
                        width="auto"
                        minW="100px"
                      />
                    </WrapItem>
                  </Wrap>
                </Box>
              </Box>
            </Box>
            <Box w="100%">
              <Heading size="md">
                <Trans>Prompt</Trans>
              </Heading>
              <Textarea
                minH="100px"
                value={promptInput}
                onChange={(e) => handlePromptChange(e.target.value)}
                placeholder="Prompt..."
              />
            </Box>
          </VStack>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button onClick={onClose}>
            <Trans>Cancel</Trans>
          </Button>
          <Button
            primary
            isDisabled={isDisabled}
            onClick={() => {
              handleAddOrEditQueuePromptPair(newQueues, promptInput)
              onClose()
            }}
          >
            {isNew ? <Trans>Add</Trans> : <Trans>Update</Trans>}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
